/**
 * 商品説明内のプルダウン用マークアップ（div.pull-down + details.pull-down）を
 * タブ切り替え UI に組み替える。該当構造がない説明はそのまま。
 */
(function () {
  const READY = 'data-product-description-tabs-ready';

  /**
   * @param {HTMLDetailsElement} detailsEl
   * @returns {'ingredients' | 'usage' | null}
   */
  function classifyDetails(detailsEl) {
    const text = (detailsEl.querySelector('summary')?.textContent || '').trim();
    const lower = text.toLowerCase();
    if (text.includes('成分') || lower.includes('ingredient')) return 'ingredients';
    if (/使用方法|使い方/.test(text) || /how to use|directions?|usage/i.test(lower)) return 'usage';
    return null;
  }

  /**
   * @param {HTMLDetailsElement} detailsEl
   * @returns {HTMLElement}
   */
  function moveDetailsBody(detailsEl) {
    const wrap = document.createElement('div');
    wrap.className = 'product-tabs__panel-inner';
    for (const child of [...detailsEl.children]) {
      if (child.tagName !== 'SUMMARY') wrap.appendChild(child);
    }
    detailsEl.remove();
    return wrap;
  }

  /**
   * @param {HTMLElement} root
   */
  function initDescriptionTabs(root) {
    if (root.getAttribute(READY) === 'true') return;

    const aboutDiv = Array.from(root.querySelectorAll('div.pull-down')).find((d) => !d.closest('details'));
    const detailsEls = Array.from(root.querySelectorAll('details.pull-down'));

    if (!aboutDiv || detailsEls.length < 2) return;

    let ingredientsEl = null;
    let usageEl = null;
    for (const d of detailsEls) {
      const kind = classifyDetails(d);
      if (kind === 'ingredients') ingredientsEl = d;
      if (kind === 'usage') usageEl = d;
    }

    if (detailsEls.length >= 2) {
      if (!ingredientsEl || !usageEl) {
        const first = detailsEls[0];
        const second = detailsEls[1];
        const firstKind = classifyDetails(first);
        const secondKind = classifyDetails(second);
        if (!usageEl && !ingredientsEl) {
          usageEl = first;
          ingredientsEl = second;
        } else if (!usageEl) {
          usageEl = firstKind === 'usage' ? first : secondKind === 'usage' ? second : detailsEls.find((d) => d !== ingredientsEl);
        } else if (!ingredientsEl) {
          ingredientsEl =
            firstKind === 'ingredients' ? first : secondKind === 'ingredients' ? second : detailsEls.find((d) => d !== usageEl);
        }
      }
    }

    if (!ingredientsEl || !usageEl || ingredientsEl === usageEl) return;

    const labels = {
      about: root.dataset.tabLabelAbout || '商品について',
      ingredients: root.dataset.tabLabelIngredients || '成分',
      usage: root.dataset.tabLabelUsage || '使い方',
    };

    const regionLabel = root.dataset.tabRegionLabel || '';
    const baseId = root.dataset.tabsId || 'product-desc-tabs';

    const styles = Array.from(root.querySelectorAll('style'));
    styles.forEach((s) => s.remove());

    const region = document.createElement('div');
    region.className = 'product-tabs';
    if (regionLabel) {
      region.setAttribute('role', 'region');
      region.setAttribute('aria-label', regionLabel);
    }

    const tablist = document.createElement('div');
    tablist.className = 'product-tabs__list';
    tablist.setAttribute('role', 'tablist');

    const panelsWrap = document.createElement('div');
    panelsWrap.className = 'product-tabs__panels';

    /** @type {HTMLButtonElement[]} */
    const tabs = [];
    /** @type {HTMLElement[]} */
    const panels = [];

    /**
     * @param {number} index
     * @param {string} label
     * @param {HTMLElement} contentEl
     */
    function addTab(index, label, contentEl) {
      const tid = `${baseId}-tab-${index}`;
      const pid = `${baseId}-panel-${index}`;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'product-tabs__tab';
      btn.setAttribute('role', 'tab');
      btn.id = tid;
      btn.setAttribute('aria-controls', pid);
      btn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      btn.tabIndex = index === 0 ? 0 : -1;
      btn.textContent = label;

      const panel = document.createElement('div');
      panel.className = 'product-tabs__panel rte';
      panel.setAttribute('role', 'tabpanel');
      panel.id = pid;
      panel.setAttribute('aria-labelledby', tid);
      panel.hidden = index !== 0;
      panel.appendChild(contentEl);

      tablist.appendChild(btn);
      panelsWrap.appendChild(panel);
      tabs.push(btn);
      panels.push(panel);
    }

    addTab(0, labels.about, aboutDiv);
    addTab(1, labels.ingredients, moveDetailsBody(ingredientsEl));
    addTab(2, labels.usage, moveDetailsBody(usageEl));

    root.querySelectorAll('details.pull-down, div.pull-down').forEach((n) => n.remove());

    while (root.firstChild) {
      root.removeChild(root.firstChild);
    }
    styles.forEach((s) => root.appendChild(s));
    region.appendChild(tablist);
    region.appendChild(panelsWrap);
    root.appendChild(region);

    /**
     * @param {number} index
     */
    function selectTab(index) {
      tabs.forEach((tab, idx) => {
        const on = idx === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        panels[idx].hidden = !on;
      });
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectTab(index));
    });

    tablist.addEventListener('keydown', (e) => {
      const current = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      if (current < 0) return;
      let next = current;
      if (e.key === 'ArrowRight') {
        next = (current + 1) % tabs.length;
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        next = (current - 1 + tabs.length) % tabs.length;
        e.preventDefault();
      } else if (e.key === 'Home') {
        next = 0;
        e.preventDefault();
      } else if (e.key === 'End') {
        next = tabs.length - 1;
        e.preventDefault();
      }
      if (next !== current) {
        selectTab(next);
        tabs[next].focus();
      }
    });

    root.setAttribute(READY, 'true');
  }

  function initAll() {
    document.querySelectorAll('[data-product-description-tabs]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      initDescriptionTabs(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('product-info:loaded', initAll);
})();
